import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import {
  BodyTextBold,
  DataAmount,
  FeedbackNotification,
  GroupSeparator,
  SmallText,
  Spinner,
} from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { fetchPendingPayments, parseApiError } from '../api';
import type { PendingPayment } from '../schema';
import {
  CenteredPlaceholder,
  CircularTimer,
  EmptyState,
  PageContainer,
  PageHeader,
} from '../../../shared/components';
import { FLOW_ID, flowPath, formatAmount } from '../shared';

/** Result of a decision, surfaced as a banner when we return to the list. */
export type ResultBannerState = {
  status: 'success' | 'error';
  message: string;
};

const REFRESH_MS = 30_000;

type Phase = 'loading' | 'ready' | 'error' | 'forbidden' | 'session-expired';

// Inherits the .lum-web-container page width (1140px) and responsive gutter
// from PageContainer; top spacing comes from PageHeader's own margin.
const Page = styled(PageContainer)`
  display: flex;
  flex-direction: column;
`;

// PageHeader bakes its own vertical spacing (spacing('my', 8) = 32px); the
// gap lives here so it doesn't stack on top of that margin under the header.
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Result feedback: a compact card centered in the normal flow, above the page
// title (it takes its own space and pushes the header down — not a floating
// overlay), matching the Flowable original. Static until the user dismisses it
// with the close icon (SEB-1618).
const ResultFeedback = styled.div`
  align-self: center;
  width: 558px;
  max-width: 100%;
  margin: 8px 0 24px;
`;

// The separator labels the list directly beneath it: they sit flush (0 gap),
// like the native panel rows (SEB-1615). The result feedback sits inline above
// the header (see ResultFeedback), not here (SEB-1618).
const Group = styled.div`
  display: flex;
  flex-direction: column;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.general_dividers as string};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.forms_default_focus_primary_font as string};
    outline-offset: -2px;
    border-radius: 8px;
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 56px 0;
`;

export const PendingList: React.FC = () => {
  const bridge = useBridge();
  const navigate = useNavigate();
  const location = useLocation();
  const { signalReady } = useFlowReady();

  const [phase, setPhase] = useState<Phase>('loading');
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [banner, setBanner] = useState<ResultBannerState | null>(
    (location.state as ResultBannerState | null) ?? null,
  );

  const locale = bridge.locale ?? 'en';

  // Emit step:change once on mount (bypassing StepWizard).
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'pending',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal → cancel the embed (leaving the inbox).
  useEffect(() => {
    return bridge.onBackRequest(() => {
      bridge.emit({ type: 'cancel', flowId: FLOW_ID });
    });
  }, [bridge]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setPhase('loading');
      try {
        const items = await fetchPendingPayments();
        const actionable = items.filter((p) => p.timerInfo.timeRemaining > 0);
        setPayments(actionable);
        setPhase('ready');
        if (actionable.length > 0) {
          bridge.emit({
            type: 'telemetry',
            event: 'card-payment-sca.list.loaded',
            props: { count: actionable.length },
          });
        } else {
          bridge.emit({ type: 'telemetry', event: 'card-payment-sca.list.empty' });
        }
      } catch (e) {
        const { status } = parseApiError(e);
        bridge.emit({
          type: 'telemetry',
          event: 'card-payment-sca.list.error',
          props: { status: status ?? 0 },
        });
        // A background refresh failure must not blow away an already-shown list.
        if (silent) return;
        if (status === 403) setPhase('forbidden');
        else if (status === 401) setPhase('session-expired');
        else setPhase('error');
      }
    },
    [bridge],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Drop the host overlay once the first load settles (manual readiness).
  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  // Silent 30 s refresh — authoritative source for the pending set (O-4).
  useEffect(() => {
    const id = setInterval(() => void load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Token-expiry recovery: when the host refreshes the token, retry the load.
  useEffect(() => {
    if (phase !== 'session-expired') return;
    if (!bridge.token) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge.token]);

  const openItem = useCallback(
    (id: string) => {
      bridge.emit({
        type: 'telemetry',
        event: 'card-payment-sca.item.opened',
        props: { notification_id: id },
      });
      navigate(flowPath(`approve-payment/${id}`));
    },
    [bridge, navigate],
  );

  const handleExpired = useCallback(
    (id: string) => {
      bridge.emit({
        type: 'telemetry',
        event: 'card-payment-sca.timer.expired',
        props: { notification_id: id },
      });
      // Server is authoritative for the pending set (O-4): reconcile via an
      // immediate silent refresh instead of optimistically removing the row,
      // which fights the next refresh and resurrects items on clock skew.
      void load(true);
    },
    [bridge, load],
  );

  if (phase === 'loading') {
    return (
      <Page>
        <CenteredSpinner>
          <Spinner />
        </CenteredSpinner>
      </Page>
    );
  }

  if (phase === 'forbidden' || phase === 'session-expired' || phase === 'error') {
    // A failed list load shows the error placeholder with no action — the user
    // is already on the list, there's nowhere to go back to (SEB-1618).
    const message =
      phase === 'forbidden'
        ? t`You don't have permission to approve payments.`
        : phase === 'session-expired'
          ? t`Your session has expired. Please reopen this screen.`
          : t`We couldn't load your pending payments. Please try again.`;
    return (
      <Page>
        <CenteredPlaceholder>
          <EmptyState variant="error" title={t`Error`} description={message} />
        </CenteredPlaceholder>
      </Page>
    );
  }

  return (
    <Page>
      {banner && (
        <ResultFeedback>
          <FeedbackNotification
            variant={banner.status}
            message={banner.message}
            hasCloseIcon
            onClose={() => setBanner(null)}
          />
        </ResultFeedback>
      )}

      <PageHeader title={t`Your Payments`} showDivider={false} bottomSpacing={6} topSpacing={4} />

      <Content>
        <Group>
          <GroupSeparator label={t`Pending Approvals`} />

          {payments.length === 0 ? (
            <div style={{ marginTop: 22 }}>
              <EmptyState title={t`You're all set`} description={t`Nothing left to approve`} />
            </div>
          ) : (
            <List>
              {payments.map((p) => (
                <Row
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openItem(p.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openItem(p.id);
                    }
                  }}
                >
                  <RowMain>
                    <BodyTextBold>{p.title}</BodyTextBold>
                    {p.transactionChannel && <SmallText>{p.transactionChannel}</SmallText>}
                  </RowMain>
                  <RowRight>
                    <DataAmount
                      label=""
                      value={formatAmount(p.amountNum, locale)}
                      currency={p.currency}
                      size="m"
                      weight="bold"
                    />
                    <CircularTimer
                      timeRemaining={p.timerInfo.timeRemaining}
                      timeFull={p.timerInfo.timeFull}
                      onExpire={() => handleExpired(p.id)}
                    />
                  </RowRight>
                </Row>
              ))}
            </List>
          )}
        </Group>
      </Content>
    </Page>
  );
};
